import {
	type AccountInfo,
	type AuthenticationResult,
	PublicClientApplication,
	type RedirectRequest,
} from '@azure/msal-browser';
import runtimeConfig from './runtimeConfig';

let _msEntraAuth: PublicClientApplication | null = null;
let _initializePromise: Promise<void> | null = null;

const getScopes = (): string[] => {
	const scopes = runtimeConfig.msEntra.scopes.split(' ').filter(Boolean);
	return scopes.length > 0 ? scopes : ['openid', 'profile', 'email'];
};

// MSAL v3 requires initialize() to complete before any other API call
const getInitializedMsEntraAuth =
	async (): Promise<PublicClientApplication | null> => {
		const msEntraAuth = getMsEntraAuthInstance();
		if (!msEntraAuth) {
			return null;
		}
		if (!_initializePromise) {
			_initializePromise = msEntraAuth.initialize();
		}
		await _initializePromise;
		return msEntraAuth;
	};

/**
 * MS Entra (Azure AD) configuration
 */
interface MsEntraConfig {
	clientId: string;
	authority: string;
	redirectUri: string;
	scopes: string[];
}

/**
 * Get or initialize MS Entra PublicClientApplication instance
 * Lazy initialization ensures runtime config is loaded
 */
const getMsEntraAuthInstance = (): PublicClientApplication | null => {
	if (_msEntraAuth !== null) {
		return _msEntraAuth;
	}

	// Check if MS Entra is configured
	const isMsEntraConfigured = !!(
		runtimeConfig.msEntra.clientId && runtimeConfig.msEntra.tenantId
	);

	if (!isMsEntraConfigured) {
		return null;
	}

	const msEntraConfig: MsEntraConfig = {
		clientId: runtimeConfig.msEntra.clientId || '',
		authority:
			runtimeConfig.msEntra.authority ||
			`https://login.microsoftonline.com/${runtimeConfig.msEntra.tenantId}`,
		redirectUri:
			runtimeConfig.msEntra.redirectUri ||
			`${window.location.origin}/login/callback`,
		scopes: getScopes(),
	};

	// Create MSAL configuration
	const msalConfig = {
		auth: {
			clientId: msEntraConfig.clientId,
			authority: msEntraConfig.authority,
			redirectUri: msEntraConfig.redirectUri,
			// Stay on /login/callback after the redirect so handleRedirectPromise
			// can process the response there (the default navigates back to the
			// originating page, which never calls handleRedirectPromise)
			navigateToLoginRequestUrl: false,
		},
		cache: {
			cacheLocation: 'sessionStorage',
			storeAuthStateInCookie: false,
		},
	};

	// Create MSAL application instance
	_msEntraAuth = new PublicClientApplication(msalConfig);

	return _msEntraAuth;
};

/**
 * Check if MS Entra is properly configured
 */
export const isMsEntraEnabled = () => {
	return getMsEntraAuthInstance() !== null;
};

/**
 * Check if MS Entra is configured and initialized
 */
export const getMsEntraAuth = (): PublicClientApplication | null =>
	getMsEntraAuthInstance();

/**
 * Get the MS Entra account
 */
export const getMsEntraAccount = (): AccountInfo | null => {
	const msEntraAuth = getMsEntraAuthInstance();
	if (!msEntraAuth) {
		return null;
	}

	const accounts = msEntraAuth.getAllAccounts();
	return accounts.length > 0 ? accounts[0] : null;
};

/**
 * Check if user is authenticated with MS Entra
 */
export const isMsEntraAuthenticated = async (): Promise<boolean> => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		return false;
	}

	try {
		const accounts = msEntraAuth.getAllAccounts();
		if (accounts.length === 0) {
			return false;
		}

		// Check if we have a valid access token
		const tokenResponse = await msEntraAuth.acquireTokenSilent({
			scopes: getScopes(),
			account: accounts[0],
		});

		return !!tokenResponse?.accessToken;
	} catch (error) {
		console.error('Error checking MS Entra authentication:', error);
		return false;
	}
};

/**
 * Initiate MS Entra login flow
 */
export const loginWithMsEntra = async (): Promise<void> => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		throw new Error(
			'MS Entra is not configured. Please set MS_ENTRA_CLIENT_ID and MS_ENTRA_TENANT_ID environment variables.',
		);
	}

	try {
		const loginRequest: RedirectRequest = {
			scopes: getScopes(),
			redirectUri:
				runtimeConfig.msEntra.redirectUri ||
				`${window.location.origin}/login/callback`,
		};

		await msEntraAuth.loginRedirect(loginRequest);
	} catch (error) {
		console.error('MS Entra login error:', error);
		throw error;
	}
};

/**
 * Handle the callback after MS Entra authentication
 */
export const handleMsEntraCallback = async (): Promise<string | null> => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		throw new Error('MS Entra is not configured');
	}

	try {
		const tokenResponse = await msEntraAuth.handleRedirectPromise();
		if (!tokenResponse) {
			console.error('No token response from MS Entra callback');
			return null;
		}

		return tokenResponse.accessToken;
	} catch (error) {
		console.error('Error handling MS Entra callback:', error);
		throw error;
	}
};

/**
 * Get MS Entra user information from ID token claims
 */
export const getMsEntraUser = async () => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		throw new Error('MS Entra is not configured');
	}

	try {
		const accounts = msEntraAuth.getAllAccounts();
		if (accounts.length === 0) {
			throw new Error('No MS Entra accounts found');
		}

		const account = accounts[0];

		// Get access token to extract claims
		const tokenResponse = await msEntraAuth.acquireTokenSilent({
			scopes: getScopes(),
			account: account,
		});

		if (!tokenResponse?.idTokenClaims) {
			throw new Error('No ID token claims available');
		}

		// MSAL types idTokenClaims as a plain object; narrow to the claims we read
		const claims = tokenResponse.idTokenClaims as {
			name?: string;
			preferred_username?: string;
			email?: string;
			upn?: string;
			oid?: string;
			sub?: string;
		};

		// Transform MS Entra claims to Okta-like format for compatibility
		return {
			name: claims.name || '',
			preferred_username:
				claims.preferred_username || claims.email || claims.upn || '',
			email: claims.email || claims.preferred_username || '',
			sub: claims.oid || claims.sub || '',
			picture: undefined, // MS Entra doesn't typically provide picture in ID token
		};
	} catch (error) {
		console.error('Error getting MS Entra user:', error);
		throw error;
	}
};

/**
 * Get MS Entra access token
 */
export const getMsEntraToken = async (
	forceRefresh = false,
): Promise<string | null> => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		return null;
	}

	try {
		const accounts = msEntraAuth.getAllAccounts();
		if (accounts.length === 0) {
			return null;
		}

		const tokenResponse = await msEntraAuth.acquireTokenSilent({
			scopes: getScopes(),
			account: accounts[0],
			forceRefresh,
		});

		return tokenResponse?.accessToken || null;
	} catch (error) {
		console.error('Error getting MS Entra token:', error);
		return null;
	}
};

/**
 * Get valid MS Entra access token with expiration check
 */
export const getValidMsEntraAccessToken = async (): Promise<string | null> => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		return null;
	}

	try {
		const accounts = msEntraAuth.getAllAccounts();
		if (accounts.length === 0) {
			return null;
		}

		const tokenResponse = await msEntraAuth.acquireTokenSilent({
			scopes: getScopes(),
			account: accounts[0],
		});

		if (!tokenResponse?.accessToken) {
			return null;
		}

		// Check if token is expired or about to expire (within 30 minutes)
		const now = Math.floor(Date.now() / 1000);
		const expiresAt = tokenResponse.expiresOn
			? Math.floor(tokenResponse.expiresOn.getTime() / 1000)
			: 0;

		if (expiresAt - now <= 1800) {
			// The cached token is near expiry; force MSAL to fetch a fresh one
			const renewedToken = await getMsEntraToken(true);
			return renewedToken || tokenResponse.accessToken;
		}

		return tokenResponse.accessToken;
	} catch (error) {
		console.error('Error in MS Entra token validation:', error);
		return null;
	}
};

/**
 * Logout from MS Entra
 */
export const logoutFromMsEntra = async () => {
	const msEntraAuth = await getInitializedMsEntraAuth();
	if (!msEntraAuth) {
		return; // Silently return if MS Entra is not configured
	}

	try {
		const accounts = msEntraAuth.getAllAccounts();

		// Logout redirect clears the MSAL cache for the account and ends the
		// session at the IdP; passing the account avoids the account picker
		await msEntraAuth.logoutRedirect({
			account: accounts.length > 0 ? accounts[0] : null,
			postLogoutRedirectUri: window.location.origin,
		});
	} catch (error) {
		console.error('Error logging out from MS Entra:', error);
		throw error;
	}
};

// Export for backward compatibility (will be null if not configured)
export const msEntraAuth = getMsEntraAuthInstance();

export default msEntraAuth;
